import { useState, type FormEvent } from 'react';
import { Button, DialogActions, DialogContent, Tab, Tabs } from '@mui/material';
import { ADMIN_PAGE_LABELS as LABELS } from '../../../utils/labels';
import AppDialog from '../../../components/AppDialog';
import { useModalHistory } from '../../../utils/useModalHistory';
import SpinnerButton from '../../../components/SpinnerButton';
import LimitField from './LimitField';
import ToggleField from './ToggleField';
import type { OrgWithMembers, OrgSettingsInput } from '../types';

type TabKey = 'limits' | 'settings';

interface OrgSettingsModalProps {
  org: OrgWithMembers;
  onClose: () => void;
  onSave: (settings: OrgSettingsInput) => Promise<boolean>;
}

export default function OrgSettingsModal({ org, onClose, onSave }: OrgSettingsModalProps) {
  useModalHistory(onClose);
  const [tab, setTab] = useState<TabKey>('limits');
  const [projectsUnlimited, setProjectsUnlimited] = useState(org.max_active_projects == null);
  const [projectsLimit, setProjectsLimit] = useState(org.max_active_projects != null ? String(org.max_active_projects) : '');
  const [projectsError, setProjectsError] = useState('');
  const [pickupHandinEnabled, setPickupHandinEnabled] = useState(org.pickup_handin_enabled);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    let maxActiveProjects: number | null = null;
    if (!projectsUnlimited) {
      const parsed = Number(projectsLimit);
      if (projectsLimit.trim() === '' || !Number.isInteger(parsed) || parsed < 0) {
        setProjectsError(LABELS.limitInvalid);
        setTab('limits');
        return;
      }
      maxActiveProjects = parsed;
    }

    setSaving(true);
    const ok = await onSave({ max_active_projects: maxActiveProjects, pickup_handin_enabled: pickupHandinEnabled });
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <AppDialog
      open
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      title={LABELS.limitsModalTitle}
      confirmLabel={LABELS.save}
      confirmFormId="org-settings-form"
      confirmDisabled={saving}
      confirmLoading={saving}
      testId="org-settings-modal"
    >
      <form id="org-settings-form" onSubmit={handleSubmit} noValidate className="flex flex-col min-h-0">
        <Tabs value={tab} onChange={(_, value: TabKey) => setTab(value)} className="border-b border-border px-6">
          <Tab label={LABELS.tabLimits} value="limits" />
          <Tab label={LABELS.tabSettings} value="settings" />
        </Tabs>
        <DialogContent className="flex flex-col gap-4">
          {tab === 'limits' && (
            <LimitField
              title={LABELS.maxActiveProjectsTitle}
              description={LABELS.maxActiveProjectsDescription}
              fieldLabel={LABELS.maxActiveProjectsLabel}
              unlimited={projectsUnlimited}
              onUnlimitedChange={setProjectsUnlimited}
              value={projectsLimit}
              onValueChange={(v) => { setProjectsLimit(v); if (projectsError) setProjectsError(''); }}
              error={projectsError}
            />
          )}
          {tab === 'settings' && (
            <ToggleField
              title={LABELS.pickupHandinEnabledTitle}
              description={LABELS.pickupHandinEnabledDescription}
              switchLabel={LABELS.pickupHandinEnabledSwitchLabel}
              checked={pickupHandinEnabled}
              onChange={setPickupHandinEnabled}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit" data-testid="cancel-button">{LABELS.cancel}</Button>
          <SpinnerButton type="submit" variant="contained" loading={saving} data-testid="save-button">
            {LABELS.save}
          </SpinnerButton>
        </DialogActions>
      </form>
    </AppDialog>
  );
}
